import React from 'react';
import { MdClose, MdExpandMore, MdSearch, MdCheck } from 'react-icons/md';

export default function MultiSelectSearchableDropdown({
	label,
	placeholder = 'Select items',
	options = [],
	value = [],
	onChange,
	className = '',
	disabled = false,
	loading = false,
	required = false,
	emptyMessage = 'No results found',
	maxVisibleTags = 2,
	showSearch,
	searchable,
	isSearchable,
	error,
}) {
	const wrapperRef = React.useRef(null);
	const searchInputRef = React.useRef(null);

	const [isOpen, setIsOpen] = React.useState(false);
	const [searchText, setSearchText] = React.useState('');
	const [placement, setPlacement] = React.useState('bottom');
	const [menuMaxHeight, setMenuMaxHeight] = React.useState(280);

	React.useLayoutEffect(() => {
		if (!isOpen || !wrapperRef.current) return;
		const updatePlacement = () => {
			if (!wrapperRef.current) return;
			const rect = wrapperRef.current.getBoundingClientRect();
			const viewportHeight = window.innerHeight;
			const spaceBelow = viewportHeight - rect.bottom - 16;
			const spaceAbove = rect.top - 16;
			const defaultMax = 280;

			if (spaceBelow < 200 && spaceAbove > spaceBelow) {
				setPlacement('top');
				setMenuMaxHeight(Math.min(defaultMax, Math.max(140, spaceAbove)));
			} else {
				setPlacement('bottom');
				setMenuMaxHeight(Math.min(defaultMax, Math.max(140, spaceBelow)));
			}
		};

		updatePlacement();
		window.addEventListener('resize', updatePlacement);
		window.addEventListener('scroll', updatePlacement, true);
		return () => {
			window.removeEventListener('resize', updatePlacement);
			window.removeEventListener('scroll', updatePlacement, true);
		};
	}, [isOpen]);

	const shouldShowSearch =
		showSearch !== undefined
			? Boolean(showSearch)
			: searchable !== undefined
			? Boolean(searchable)
			: isSearchable !== undefined
			? Boolean(isSearchable)
			: options.length > 5;

	const selectedValues = React.useMemo(() => {
		if (Array.isArray(value)) return value.map(String);
		if (value === null || value === undefined || value === '') return [];
		return [String(value)];
	}, [value]);

	const selectedOptions = React.useMemo(() => {
		return options.filter((option) => selectedValues.includes(String(option.value)));
	}, [options, selectedValues]);

	const filteredOptions = React.useMemo(() => {
		const term = searchText.trim().toLowerCase();

		if (!term) {
			return options;
		}

		return options.filter((option) => String(option.label || '').toLowerCase().includes(term));
	}, [options, searchText]);

	React.useEffect(() => {
		if (!isOpen) {
			setSearchText('');
			return;
		}

		const timer = window.setTimeout(() => {
			if (shouldShowSearch) {
				searchInputRef.current?.focus();
			}
		}, 0);

		return () => window.clearTimeout(timer);
	}, [isOpen, shouldShowSearch]);

	React.useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleClickOutside = (event) => {
			if (!wrapperRef.current?.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	const handleSelect = (optionValue) => {
		const strValue = String(optionValue);
		const currentValues = Array.isArray(value) ? value.map(String) : [];
		const newValue = currentValues.includes(strValue)
			? currentValues.filter((v) => v !== strValue)
			: [...currentValues, strValue];
		onChange?.(newValue);
	};

	const handleRemoveTag = (optionValue, event) => {
		event.stopPropagation();
		const currentValues = Array.isArray(value) ? value.map(String) : [];
		const newValue = currentValues.filter((v) => v !== String(optionValue));
		onChange?.(newValue);
	};

	const visibleTags = selectedOptions.slice(0, maxVisibleTags);
	const hiddenCount = selectedOptions.length - visibleTags.length;

	return (
		<div className="position-relative" ref={wrapperRef}>
			{label ? (
				<label className="form-label fw-semibold mb-2">
					{label}
					{required ? <span className="text-danger ms-1">*</span> : null}
				</label>
			) : null}

			<button
				type="button"
				className={`form-control d-flex align-items-center justify-content-between text-start py-2 ${error ? 'is-invalid' : ''} ${className}`}
				onClick={() => !disabled && setIsOpen((previous) => !previous)}
				disabled={disabled}
				style={{ minHeight: 38, lineHeight: 1.3 }}
			>
				<div className="d-flex align-items-center flex-wrap gap-2" style={{ flex: 1 }}>
					{selectedOptions.length === 0 ? (
						<span className="text-secondary">{placeholder}</span>
					) : (
						<>
							{visibleTags.map((option) => (
								<span
									key={option.value}
									className="badge hc-bg-primary text-white d-flex align-items-center gap-2"
									style={{ fontSize: '0.85rem' }}
								>
									{option.label}
									<button
										type="button"
										className="btn btn-sm p-0 text-white"
										onClick={(e) => handleRemoveTag(option.value, e)}
										disabled={loading}
										style={{ lineHeight: '1' }}
									>
										<MdClose size={14} />
									</button>
								</span>
							))}
							{hiddenCount > 0 && (
								<span className="badge bg-secondary text-white" style={{ fontSize: '0.85rem' }}>
									+{hiddenCount} more
								</span>
							)}
						</>
					)}
				</div>
				<MdExpandMore size={18} className="text-secondary ms-2" />
			</button>

			<div
				className={`dropdown-menu w-100 p-2 shadow overflow-y-auto ${isOpen ? 'show' : ''}`}
				style={{
					position: 'absolute',
					zIndex: 1060,
					maxHeight: menuMaxHeight,
					...(placement === 'top'
						? { bottom: '100%', top: 'auto', marginBottom: '6px', marginTop: 0 }
						: { top: '100%', bottom: 'auto', marginTop: '6px', marginBottom: 0 }
					),
				}}
			>
				{shouldShowSearch && (
					<div className="position-relative mb-2">
						<span
							className="position-absolute top-50 translate-middle-y text-secondary"
							style={{ left: 10, lineHeight: 0 }}
						>
							<MdSearch size={16} />
						</span>
						<input
							ref={searchInputRef}
							type="text"
							className="form-control form-control-sm"
							placeholder="Search..."
							value={searchText}
							onChange={(event) => setSearchText(event.target.value)}
							onClick={(event) => event.stopPropagation()}
							disabled={loading}
							style={{ paddingLeft: 30 }}
						/>
					</div>
				)}
				{loading ? (
					<div className="px-1 py-1">
						<div className="placeholder-glow mb-2">
							<span className="placeholder col-12 rounded-2" style={{ height: 28 }} />
						</div>
						<div className="placeholder-glow mb-2">
							<span className="placeholder col-10 rounded-2" style={{ height: 28 }} />
						</div>
						<div className="placeholder-glow">
							<span className="placeholder col-11 rounded-2" style={{ height: 28 }} />
						</div>
					</div>
				) : filteredOptions.length > 0 ? (
					filteredOptions.map((option) => {
						const isSelected = selectedValues.includes(String(option.value));

						return (
							<button
								key={option.value}
								type="button"
								className={`dropdown-item rounded-2 d-flex align-items-center justify-content-between mb-2 ${
									isSelected ? 'hc-dropdown-item-selected' : ''
								}`}
								onClick={() => handleSelect(option.value)}
							>
								<span>{option.label}</span>
								{isSelected ? <MdCheck size={16} /> : null}
							</button>
						);
					})
				) : (
					<div className="px-2 py-2 text-secondary small">{emptyMessage}</div>
				)}
			</div>
			{error ? <div className="invalid-feedback d-block">{error}</div> : null}
		</div>
	);
}
